import { HttpClient, HttpResponse } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { JwtHelperService } from '@auth0/angular-jwt';
import { BehaviorSubject, firstValueFrom, Observable } from 'rxjs';
import { UserSessionService } from '../services/user-session.service';
import { LogedUser } from './models/logged-user.model';
import { environment } from '@base/environments/environment';
import { AUTH_SESSION } from './auth-flow/tokens';
import { AuthSession } from './auth-flow/auth-session';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  private readonly jwtHelper: JwtHelperService;
  private readonly currentUserSubject: BehaviorSubject<LogedUser | null>;
  public currentUser: Observable<LogedUser | null>;

  private readonly currentTokenSubject: BehaviorSubject<string | null>;
  public currentToken: Observable<string | null>;

  constructor(
    private readonly http: HttpClient,
    @Inject(AUTH_SESSION) private readonly authSession: AuthSession,
    private readonly userSessionService: UserSessionService,
  ) {
    this.currentUserSubject = new BehaviorSubject<LogedUser | null>(
      this.getUserOrNull()
    );
    this.currentUser = this.currentUserSubject.asObservable();
    this.jwtHelper = new JwtHelperService();

    this.currentTokenSubject = new BehaviorSubject<string | null>(
      this.getTokenOrNull()
    );
    this.currentToken = this.currentTokenSubject.asObservable();
  }

  private getUserOrNull() {
    const DATA = localStorage.getItem('currentUser');
    return DATA ? JSON.parse(DATA) : null;
  }

  private getTokenOrNull() {
    const DATA = localStorage.getItem('token');
    return DATA ?? null;
  }

  public get currentUserValue(): LogedUser | null {
    return this.currentUserSubject.value;
  }

  public get currentTokenValue(): string | null {
    return this.currentTokenSubject.value;
  }

  /**
   * Performs the auth
   * @param email email of user
   * @param password password of user
   */
  async login(username: string, password: string): Promise<any> {
    const DATA = await firstValueFrom(
      this.http.post<any>(environment.tokenEndpoint, {
        user: { username, password },
      })
    );

    sessionStorage.setItem('userInfo', JSON.stringify(DATA));
    this.currentUserSubject.next(DATA);
    return DATA;
  }

  public getToken(): string {
    if (!this.currentTokenValue) return '';
    return this.currentTokenValue;
  }

  public isAuthenticated(): boolean {
    const CURRENT_USER = this.currentUserValue;
    if (CURRENT_USER) {
      // Check whether the token is expired and return
      // true or false
      return !this.jwtHelper.isTokenExpired(this.getToken());
    }

    return false;
  }

  async forgotten(email: string): Promise<HttpResponse<any>> {
    const RESPONSE = await firstValueFrom(this.forgottenObservable(email));
    return RESPONSE;
  }

  forgottenObservable(email: string): Observable<HttpResponse<any>> {
    return this.http.post<HttpResponse<any>>(
      `${environment.apiEndpoint}/forgotten`,
      { email },
      {
        responseType: 'json',
        observe: 'response',
      }
    );
  }

  logout(): void {
    this.userSessionService.clear();
    this.currentUserSubject.next(null);
    this.authSession.logout();
  }
}
